import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Sparkles } from "lucide-react";
import { api } from "../../services/api";
import AstriaJapanV2Shell, {
  SHELL_COLORS_V2,
  JP2_FONT,
} from "./AstriaJapanV2Shell";
import { getStoredAstriaJapanDob } from "../../utils/astriaJapanKyusei";

// ─── Astria Japan Companion — Inner-Space Tab ──────────────────────────
// This is the "quietest" screen in JP lane.
// No images, no icons. One soft card, lots of white space.
// Text is multi-line, gentle, not dense.

const JP_FONT = JP2_FONT;
const C = {
  ...SHELL_COLORS_V2,
  textPrimary: "#2B211D",
  textSecondary: "#5A4A42",
  textMuted: "#8A7A73",
  gradientStart: "#FDF7F5",
  gradientEnd: "#FFF0EC",
  companionBg: "#FAFAFA",
};

// ─── Fallback content if API fails ──────────────────────────────────────
// This ensures the tab always shows something meaningful
const FALLBACK_MESSAGES = {
  ja: [
    "今日は、心の奥からそっと流れてくる言葉に耳を傾けてみましょう。",
    "静かな時間の中で、あなたの心が何を感じているのか、一緒に見つめてみましょう。",
    "あなたの内側にある静かな知恵が、今日の道しるべとなりますように。",
    "心の声は、いつもあなたのそばにあります。ただ、静かに耳を澄ませてみてください。",
    "今日という日が、あなたにとって穏やかな一日でありますように。",
    "呼吸を整えて、今この瞬間の自分を感じてみてください。",
  ],
  en: [
    "Today, gently listen to the words flowing from within your heart.",
    "In the quiet moments, let's observe what your heart is feeling.",
    "May the quiet wisdom within you be your guide today.",
    "Your heart's voice is always with you. Just take a moment to listen.",
    "May today be a gentle day for your heart.",
    "Take a breath and feel yourself in this moment.",
  ],
};

interface CompanionResponse {
  success: boolean;
  starId: number;
  view: {
    header: { title: string; subtext: string };
    sections: Array<{ id: string; title: string; type: string; data: any }>;
  };
}

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

const JPCompanionResult: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<CompanionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isNavigating, setIsNavigating] = useState(false);

  // Get language preference
  const getPreferredLanguage = (): "ja" | "en" => {
    const lang = localStorage.getItem("preferredLanguage") || "ja";
    return lang === "en" ? "en" : "ja";
  };

  const language = getPreferredLanguage();

  // Language-specific content
  const content = {
    ja: {
      brand: "インナー・スペース",
      title: "Astria Japan · インナー・スペース",
      subtext: "心の奥の静かな流れを、一緒にそっと感じるための場所です。",
      refresh: "もう一度",
      error: "インナー・スペースの情報が見つかりませんでした。",
      loading: "読み込み中...",
      back: "インナー・スペースを見る",
      footer: "あなたの心に、そっと寄り添う時間を。",
      noDob: "生年月日を設定してください。",
    },
    en: {
      brand: "Inner Space",
      title: "Astria Japan · Inner Space",
      subtext: "A quiet space to gently feel the flow within your heart.",
      refresh: "Refresh",
      error: "Inner Space information not found.",
      loading: "Loading...",
      back: "View Inner Space",
      footer: "A gentle moment for your heart.",
      noDob: "Please set your date of birth.",
    },
  };

  const t = content[language];
  const fallbackMessages = FALLBACK_MESSAGES[language];

  useEffect(() => {
    const fetchCompanionData = async () => {
      try {
        // 1. Check session storage first
        const raw = sessionStorage.getItem("astriaJapanCompanion");
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (parsed.success) {
              setData(parsed);
              setLoading(false);
              return;
            }
          } catch {
            // Invalid JSON, continue to API
          }
        }

        // 2. Get stored DOB from localStorage
        const storedDob = getStoredAstriaJapanDob();

        // 3. If no DOB, check if we can get it from other tab's data
        if (!storedDob) {
          // Try to get DOB from Timing tab's stored data
          const timingRaw = sessionStorage.getItem("astriaJapanKyuseiTiming");
          if (timingRaw) {
            try {
              const timingData = JSON.parse(timingRaw);
              // If we have timing data, we can use the starId but we still need DOB
              // So we should try to fetch the companion data without DOB?
              // Actually, we need DOB for the API call

              // Check if we have a stored DOB from previous sessions
              const savedDob = localStorage.getItem("astriaJapanDob");
              if (savedDob) {
                // Use saved DOB from localStorage
                const response = await api.post(
                  "/backend/astria-japan-kyusei/companion",
                  {
                    dob: savedDob,
                  },
                );
                if (response.data?.success) {
                  sessionStorage.setItem(
                    "astriaJapanCompanion",
                    JSON.stringify(response.data),
                  );
                  setData(response.data);
                  setLoading(false);
                  return;
                }
              }
            } catch {
              // Fall through
            }
          }

          // No DOB found anywhere - redirect to form
          // But don't redirect immediately - show a message first
          setError(t.noDob);
          setLoading(false);
          return;
        }

        // 4. Fetch from API with the stored DOB
        const response = await api.post(
          "/backend/astria-japan-kyusei/companion",
          {
            dob: storedDob,
          },
        );

        if (response.data?.success) {
          sessionStorage.setItem(
            "astriaJapanCompanion",
            JSON.stringify(response.data),
          );
          setData(response.data);
        } else {
          setError(t.error);
        }
      } catch (err) {
        console.error("Failed to fetch companion data:", err);
        // Check if we have cached data
        const cached = sessionStorage.getItem("astriaJapanCompanion");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed.success) {
              setData(parsed);
              setLoading(false);
              return;
            }
          } catch {
            // Invalid cache
          }
        }
        // Use fallback content instead of showing error
        setError(null);
        // Create a mock response with fallback content
        const mockData: CompanionResponse = {
          success: true,
          starId: 0,
          view: {
            header: {
              title: language === "ja" ? "インナー・スペース" : "Inner Space",
              subtext:
                language === "ja"
                  ? "心の奥の静かな流れを感じる時間"
                  : "A moment to feel the quiet flow within",
            },
            sections: [
              {
                id: "innerSpaceMessage",
                title:
                  language === "ja" ? "今日のメッセージ" : "Today's Message",
                type: "jpCompanion",
                data: {
                  message:
                    fallbackMessages[
                      Math.floor(Math.random() * fallbackMessages.length)
                    ],
                },
              },
            ],
          },
        };
        setData(mockData);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanionData();
  }, [t.error, t.noDob, language, fallbackMessages]);

  const handleRefresh = () => {
    setIsNavigating(true);
    sessionStorage.removeItem("astriaJapanCompanion");
    // Navigate to form to re-enter DOB
    navigate("/astria-japan/companion");
  };

  const handleGoToForm = () => {
    setIsNavigating(true);
    navigate("/astria-japan/companion");
  };

  // Get the inner space message - from API or fallback
  const getInnerSpaceMessage = (): string => {
    if (data?.view?.sections) {
      const section = data.view.sections.find(
        (s) => s.id === "innerSpaceMessage",
      );
      if (section?.data?.message) {
        return section.data.message;
      }
    }
    // Return a random fallback message
    const randomIndex = Math.floor(Math.random() * fallbackMessages.length);
    return fallbackMessages[randomIndex];
  };

  const innerSpaceMessage = getInnerSpaceMessage();

  // Show loading state
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: C.bg, fontFamily: JP_FONT }}
      >
        <div
          className="w-8 h-8 rounded-full border-2 animate-spin"
          style={{ borderColor: `${C.accent}33`, borderTopColor: C.accent }}
        />
        <span className="ml-3 text-sm" style={{ color: C.textMuted }}>
          {t.loading}
        </span>
      </div>
    );
  }

  // Show error with redirect to form
  if (error === t.noDob || error === "No DOB found") {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: C.bg, fontFamily: JP_FONT }}
      >
        <div className="text-center max-w-sm">
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: C.accentSoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <Sparkles className="w-5 h-5" style={{ color: C.accent }} />
          </div>
          <p className="text-sm mb-4" style={{ color: C.textSecondary }}>
            {language === "ja"
              ? "インナー・スペースを開くには、生年月日を設定してください。"
              : "Please set your date of birth to open Inner Space."}
          </p>
          <button
            onClick={handleGoToForm}
            className="px-6 py-2.5 rounded-full text-white text-xs font-semibold transition-all hover:opacity-90"
            style={{ background: C.accent }}
          >
            {language === "ja" ? "生年月日を設定" : "Set Date of Birth"}
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: C.bg, fontFamily: JP_FONT }}
      >
        <div className="text-center">
          <p className="text-sm mb-4" style={{ color: "#B4574A" }}>
            {error}
          </p>
          <button
            onClick={() => navigate("/astria-japan/companion")}
            className="px-6 py-2.5 rounded-full text-white text-xs font-semibold"
            style={{ background: C.accent }}
          >
            {t.back}
          </button>
        </div>
      </div>
    );
  }

  return (
    <AstriaJapanV2Shell active="companion">
      {/* ─── Header ────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          marginBottom: 28,
          padding: "28px 32px",
          background: `linear-gradient(135deg, ${C.gradientStart} 0%, ${C.gradientEnd} 100%)`,
          borderRadius: 16,
          border: "1px solid rgba(212,115,93,0.08)",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: C.textMuted,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            marginBottom: 6,
          }}
        >
          {t.brand}
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 400,
            color: C.textPrimary,
            letterSpacing: "0.02em",
            lineHeight: 1.5,
          }}
        >
          {language === "ja" ? data?.view?.header?.title || t.title : t.title}
        </div>
        <p
          style={{
            fontSize: 13,
            color: C.textMuted,
            marginTop: 4,
            lineHeight: 1.6,
          }}
        >
          {language === "ja"
            ? data?.view?.header?.subtext || t.subtext
            : t.subtext}
        </p>
      </motion.div>

      {/* ─── Inner Space Message ──────────────────────────────────────── */}
      <motion.div
        custom={0}
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        style={{
          background: C.companionBg,
          borderRadius: 16,
          padding: "48px 40px",
          border: "1px solid rgba(224,121,107,0.06)",
          boxShadow: "0 2px 12px rgba(224,121,107,0.04)",
          minHeight: 220,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Subtle decorative element */}
        <div
          style={{
            marginBottom: 24,
            opacity: 0.3,
          }}
        >
          <Sparkles className="w-6 h-6" style={{ color: C.accent }} />
        </div>

        <p
          style={{
            fontSize: 17,
            lineHeight: 2.2,
            color: C.textSecondary,
            textAlign: "center",
            margin: 0,
            maxWidth: 520,
            letterSpacing: "0.03em",
            fontWeight: 400,
          }}
        >
          {innerSpaceMessage}
        </p>

        {/* Subtle divider */}
        <div
          style={{
            marginTop: 28,
            width: 40,
            height: 2,
            background: C.textMuted,
            opacity: 0.15,
            borderRadius: 2,
          }}
        />
      </motion.div>

      {/* ─── Footer ────────────────────────────────────────────────────── */}
      <motion.div
        custom={1}
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        style={{
          textAlign: "center",
          padding: "20px 16px",
          marginTop: 12,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 400,
            color: C.textMuted,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          {t.brand}
        </div>
        <div
          style={{
            fontSize: 12,
            color: C.textMuted,
            marginTop: 4,
            lineHeight: 1.6,
          }}
        >
          {t.footer}
        </div>
      </motion.div>

      {/* ─── Actions ────────────────────────────────────────────────────── */}
      <motion.div
        className="flex justify-end mt-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <button
          onClick={handleRefresh}
          disabled={isNavigating}
          className="flex items-center justify-center gap-2 rounded-full py-2.5 px-6 text-sm font-medium transition-all hover:bg-gray-50 active:scale-95"
          style={{
            border: "1px solid rgba(43,33,29,0.1)",
            color: C.textSecondary,
            background: "transparent",
            opacity: isNavigating ? 0.5 : 1,
          }}
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {t.refresh}
        </button>
      </motion.div>
    </AstriaJapanV2Shell>
  );
};

export default JPCompanionResult;
